/**
 * Resource HUD — top-left DOM overlay showing the player faction's
 * ledger + per-game-day income rate for all 7 resources (v0M3.9,
 * revised MFix3.9 to display per-day instead of per-second).
 *
 * Update per frame via `update(state)`. Cheap — only touches DOM when
 * a displayed value changes.
 */

import { injectMenuStyles } from './menu-styles';
import { computeFactionIncome, SECONDS_PER_GAME_DAY } from '../../sim';
import type { FactionId, GameState, Resources } from '../../sim';
import { getDialNumber, loadDials } from '../../data/dials';

// MFix6.6.5 (2026-07-23) — Manpower-cap dial for the HUD's "N/M" display.
// MFix16.1 (2026-07-29) — also need the generation-per-pop-per-day dial so
// the HUD can show a per-day income rate next to the manpower cell (parity
// with the other 6 resource tiles). Without this the player has no visibility
// that manpower IS regenerating when the pool has drained below 1 (which
// per-city flooring then displays as "0", hence Michael's report that
// "manpower has disappeared").
const HUD_DIALS = loadDials();
const MANPOWER_CAP_FRACTION = getDialNumber(HUD_DIALS, 'manpower_cap_fraction_of_pop');
const MANPOWER_GEN_PER_POP_PER_DAY = getDialNumber(HUD_DIALS, 'manpower_generation_per_pop_per_day');

const RESOURCE_LABELS: readonly { key: keyof Resources; label: string }[] = [
  { key: 'gold', label: 'Gold' },
  { key: 'iron', label: 'Iron' },
  { key: 'wood', label: 'Wood' },
  { key: 'horses', label: 'Horses' },
  { key: 'supplies', label: 'Supplies' },
  { key: 'learning', label: 'Learning' },
];

export interface ResourceHudHandle {
  /** Refresh the display from the current game state. Diff-based; cheap. */
  update(state: GameState): void;
  destroy(): void;
}

export function mountResourceHud(
  root: HTMLElement,
  playerFactionId: FactionId | null,
): ResourceHudHandle {
  injectMenuStyles();

  const container = document.createElement('div');
  container.className = 'su-resource-hud';

  const cells = new Map<keyof Resources, {
    amount: HTMLSpanElement;
    income: HTMLSpanElement;
  }>();

  for (const { key, label } of RESOURCE_LABELS) {
    const cell = document.createElement('div');
    cell.className = 'su-resource-cell';
    const name = document.createElement('span');
    name.className = 'su-resource-name';
    name.textContent = label;
    const amount = document.createElement('span');
    amount.className = 'su-resource-amount';
    amount.textContent = '0';
    const income = document.createElement('span');
    income.className = 'su-resource-income su-neutral';
    income.textContent = '+0.0/d';
    cell.appendChild(name);
    cell.appendChild(amount);
    cell.appendChild(income);
    container.appendChild(cell);
    cells.set(key, { amount, income });
  }

  // MFix6.6.5 (2026-07-23) — Manpower cell. Manpower is per-city, not
  // per-faction; the HUD sums across every city owned by the player
  // faction. Display: "current / cap". Cap = sum of per-city caps
  // (population × manpower_cap_fraction_of_pop).
  const manpowerCell = document.createElement('div');
  manpowerCell.className = 'su-resource-cell';
  const mpName = document.createElement('span');
  mpName.className = 'su-resource-name';
  mpName.textContent = 'Manpower';
  const mpAmount = document.createElement('span');
  mpAmount.className = 'su-resource-amount';
  mpAmount.textContent = '0';
  const mpCap = document.createElement('span');
  mpCap.className = 'su-resource-income su-neutral';
  mpCap.textContent = '/0';
  manpowerCell.appendChild(mpName);
  manpowerCell.appendChild(mpAmount);
  manpowerCell.appendChild(mpCap);
  container.appendChild(manpowerCell);

  root.appendChild(container);

  const lastAmount = new Map<keyof Resources, number | null>();
  const lastIncome = new Map<keyof Resources, number | null>();
  for (const { key } of RESOURCE_LABELS) {
    lastAmount.set(key, null);
    lastIncome.set(key, null);
  }

  function update(state: GameState): void {
    if (playerFactionId === null) return;
    const faction = state.factions.get(playerFactionId);
    if (!faction) return;
    const income = computeFactionIncome(state, playerFactionId);

    for (const { key } of RESOURCE_LABELS) {
      const cell = cells.get(key)!;
      const currentAmount = Math.floor(faction.resources[key]);
      if (currentAmount !== lastAmount.get(key)) {
        cell.amount.textContent = currentAmount.toLocaleString();
        lastAmount.set(key, currentAmount);
      }
      // MFix3.9: display per-day income (per-second income × seconds-per-game-day).
      // Round to 1 decimal for display stability.
      const currentIncome = Math.round(income[key] * SECONDS_PER_GAME_DAY * 10) / 10;
      if (currentIncome !== lastIncome.get(key)) {
        const sign = currentIncome > 0 ? '+' : '';
        cell.income.textContent = `${sign}${currentIncome.toFixed(1)}/d`;
        cell.income.className =
          currentIncome > 0
            ? 'su-resource-income su-positive'
            : currentIncome < 0
              ? 'su-resource-income su-negative'
              : 'su-resource-income su-neutral';
        lastIncome.set(key, currentIncome);
      }
    }

    // MFix6.6.5 — sum manpower + cap across player-owned cities.
    // MFix16.1 (2026-07-29) — sum THEN floor (was: floor per city then sum).
    // With Michael's lowered generation rate the per-city fractional pool
    // can sit below 1 for hours; per-city flooring hid the total across
    // multiple cities and read "0" even when the real total was e.g. 2.7.
    // Also compute per-day generation total so the player can see the pool
    // IS accumulating (same treatment as the other 6 resource tiles).
    // Note: this is the raw generation rate — it doesn't subtract
    // consumption from ZoC replenishment (which is per-cohort, not per-city,
    // and effectively unbounded to model). If Michael sees the pool falling
    // despite a positive rate here, that's the replenishment drain.
    let manpowerRaw = 0;
    let capTotal = 0;
    let genPerDay = 0;
    for (const city of state.settlements.values()) {
      const province = state.provinces.get(city.provinceId);
      if (!province || province.ownerFactionId !== playerFactionId) continue;
      if (city.siegeState !== null) continue; // Under siege → no generation this tick.
      manpowerRaw += city.manpower ?? 0;
      capTotal += Math.floor(province.population * MANPOWER_CAP_FRACTION);
      // Cities at cap contribute 0 to income (economy.ts short-circuits).
      const cap = province.population * MANPOWER_CAP_FRACTION;
      const current = city.manpower ?? 0;
      if (current < cap) genPerDay += province.population * MANPOWER_GEN_PER_POP_PER_DAY;
    }
    const manpowerTotal = Math.floor(manpowerRaw);
    const mpKey = '__manpower__' as unknown as keyof Resources;
    if (manpowerTotal !== lastAmount.get(mpKey)) {
      mpAmount.textContent = manpowerTotal.toLocaleString();
      lastAmount.set(mpKey, manpowerTotal);
    }
    // Format: "N /cap +X/d" with the /d rounded to 1 decimal like other tiles.
    const genRounded = Math.round(genPerDay * 10) / 10;
    const capKey = capTotal * 10000 + Math.round(genRounded * 10); // combined key so both trigger a rewrite
    if (capKey !== lastIncome.get(mpKey)) {
      const sign = genRounded > 0 ? '+' : '';
      mpCap.textContent = `/${capTotal.toLocaleString()} ${sign}${genRounded.toFixed(1)}/d`;
      lastIncome.set(mpKey, capKey);
    }
  }

  function destroy(): void {
    container.remove();
  }

  return { update, destroy };
}
